import { z } from 'zod'
import { productPreviewSchema } from '~/schemas'
import { fetchApi } from './helpers'

const mapProductPreview = (product: Record<string, any>) =>
  productPreviewSchema.parse({
    id: product.data?.id,
    parentId: product.data?.parent_id,
    type: product.data?.type,
    image:
      product.extra?.default_image?.extra?.url_thumb ||
      product.extra?.default_image?.data?.url ||
      product.media?.[0]?.extra?.url_thumb ||
      product.media?.[0]?.data?.url,
    path: product.extra?.url?.data?.url || '/',
    title: product.extra?.translation?.data?.name,
    intro:
      product.extra?.translation?.data?.intro ||
      product.extra?.translation?.data?.default_product_intro,
    description: product.extra?.translation?.data?.description,
    body: product.extra?.translation?.data?.body,
    startPrice: product.extra?.starting_price,
    startDate: product.data?.start_date,
    startTime: product.data?.start_time,
    endDate: product.data?.end_date,
    endTime: product.data?.end_time,
    url: product.extra?.url?.data?.url,
    venue: {
      name: product.venue?.extra?.translation?.data?.name,
      description: product.venue?.extra?.translation?.data?.description,
      address: product.venue?.extra?.translation?.data?.address,
      externalId: product.venue?.data?.external_api_id,
      googleMapsLink: product.venue?.data?.google_map_url,
      stadiumImage: product.venue?.media?.find(
        (item: Record<string, any>) =>
          item?.data?.collection_name === 'stadium',
      )?.data?.url,
    },
    extra: {
      margin: product.data?.extra?.margin,
      exchangeRate: product.data?.extra?.exchange_rate,
    },
    keyFeatureLabels: (product.keyFeatureLabels || []).map(
      (item: Record<string, any>) => ({
        name: item.extra?.translation?.data?.name,
        colour: item.data?.colour || '#E0F0FF',
      }),
    ),
    competitions: product.competitions?.map((i: any) => ({
      id: i.data?.id,
      name: i.data?.name,
    })),
    footballTeams: product.footballTeams?.map((i: any) => ({
      id: i.data?.id,
      name: i.data?.name,
    })),
    favourited: product.extra?.favourited,
    discounted: product.extra?.discounted,
    bestDropPercent: product.extra?.discount_percent,
    seoTitle: product.extra?.translation?.data?.seo_title,
  })

export const fetchProductsData = async ({
  limit,
  offset,
  ...query
}: {
  [key: string]: unknown
  limit: number
  offset: number
}) => {
  const response: any = await fetchApi(`/products`, {
    query: {
      'with[]': ['source', 'media', 'venue', 'venue.translations', 'pages.media', 'keyFeatureLabels.translations', 'urls', 'competitions'],
      limit,
      offset,
      ...query,
    },
  })

  const rawItems = Array.isArray(response) ? response : response.items
  const items = z
    .array(productPreviewSchema)
    .parse((rawItems || []).map(mapProductPreview))

  const total = z.number().parse(
    typeof response?.total === 'number' ? response.total : items.length,
  )

  return { items, total }
}

export type ProductsData = Awaited<ReturnType<typeof fetchProductsData>>

export const fetchProductData = async (productId: string) => {
  const response: any = await fetchApi(`/products/${productId}`, {
    query: {
      'with[]': [
        'source',
        'media',
        'venue',
        'venue.translations',
        'page.media',
        'page.translations',
        'keyFeatureLabels.translations',
        'urls',
        'competitions',
        'variations.translation',
      ],
    },
  })

  const product = response?.product ?? response

  return mapProductPreview(product)
}

export type ProductData = Awaited<ReturnType<typeof fetchProductData>>
